/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import NodeCollectionEffectBase from './NodeCollectionEffectBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Effect for triggering a Spine animation.
 */
@cc._decorator.ccclass
export default class AnimationEffect extends NodeCollectionEffectBase
{
    /** The [[sp.Skeleton]] to be animated. */
    @property({ type: sp.Skeleton, visible: true })
    private _skeleton: sp.Skeleton = null;

    /** The animation name to play on the [[sp.Skeleton]]. */
    @property({ visible: true })
    protected _anim: string = "";

    /** Whether or not the animation should loop. */
    @property({ visible: true })
    private _loop: boolean = false;

    /**
     * Whether or not the [[cc.Node]] of the [[sp.Skeleton]] should be set active
     * before playing the animation.
     */
    @property({ visible: true })
    private _setActive: boolean = false;

    /** Should the animation track be cleared on complete. */
    @property({ visible: true })
    private _clearOnComplete: boolean = true;

    /** Should the [[sp.Skeleton]] be reset to its start position on complete. */
    @property({ visible: true })
    private _resetPosOnComplete: boolean = true;

    private _skeletons: sp.Skeleton[] = null;
    private _completeCount: number = 0;

    protected onInitStateChange(): boolean
    {
        if (this._skeletons || !super.onInitStateChange()) { return false; }

        this._skeletons = [];
        this.reference.WithValue((nodeCollection) =>
        {
            nodeCollection.Nodes.forEach((node) => { this._skeletons.push(node.getComponent(sp.Skeleton)); });
        });

        return true;
    }

    /** Activates the effect. */
    protected trigger = (): void =>
    {
        this.prepareAnimations();

        if (this._anim === "")
        {
            this.clearSkeletons();
        }
        else
        {
            this.setSkeletonsToAnimation();
        }

        this.configureCompleteListeners();
    }

    /** Stops the in-progress effect or stops its next execution. */
    public Stop(): void
    {
        super.Stop();

        if (this.IsCurrentState())
        {
            this.onAnimEnd();
        }
    }

    protected prepareAnimations(): void
    {
        if (!this._setActive) { return; }

        this.setSkeletonNodesActive();
    }

    private configureCompleteListeners(): void
    {
        if (this._loop)
        {
            this.complete();
            return;
        }

        if (this._skeleton) { this._skeleton.setCompleteListener(this.onAnimEnd); }
        else if (this._skeletons)
        {
            this._completeCount = 0;
            this._skeletons.forEach((skeleton) => { skeleton.setCompleteListener(this.onMultiAnimsEnd); });
        }
    }

    private setSkeletonNodesActive(): void
    {
        if (this._skeleton)
        {
            this._skeleton.node.active = true;
        }
        else if (this._skeletons)
        {
            this._skeletons.forEach(skeleton => skeleton.node.active = true);
        }
    }

    private setSkeletonsToAnimation(): void
    {
        if (this._skeleton)
        {
            this._skeleton.setAnimation(0, this._anim, this._loop);
        }
        else if (this._skeletons)
        {
            this._skeletons.forEach((skeleton) => { skeleton.setAnimation(0, this._anim, this._loop); });
        }
    }

    private clearSkeletons(): void
    {
        if (this._skeleton)
        {
            this._skeleton.clearTracks();
            this._skeleton.setToSetupPose();
            this._skeleton.updateWorldTransform();
        }
        else if (this._skeletons)
        {
            this._skeletons.forEach((skeleton) =>
            {
                skeleton.clearTracks();
                skeleton.setToSetupPose();
                skeleton.updateWorldTransform();
            });
        }
    }

    /** @hidden */
    private onAnimEnd = (): void =>
    {
        this._skeleton.setCompleteListener(null);

        if (this._clearOnComplete)
        {
            this._skeleton.clearTrack(0);
        }

        if (this._resetPosOnComplete)
        {
            this._skeleton.setToSetupPose();
            this._skeleton.updateWorldTransform();
        }

        this.complete();
    }

    /** @hidden */
    private onMultiAnimsEnd = (): void =>
    {
        this._completeCount++;
        if (this._completeCount < this._skeletons.length) { return; }

        this._skeletons.forEach((skeleton) =>
        {
            skeleton.setCompleteListener(null);

            if (this._clearOnComplete) { skeleton.clearTrack(0); }

            if (this._resetPosOnComplete)
            {
                skeleton.setToSetupPose();
                skeleton.updateWorldTransform();
            }
        });

        this.complete();
    }
}
